import { db, auth } from '../firebase/config.js'
import { collection, addDoc, query, where, orderBy, limit, getDocs } from 'firebase/firestore'

export function useAuditLog() {
    const logAction = async (action, entity, entityId, changes = {}, metadata = {}) => {
        if (!auth.currentUser) {
            console.warn('Cannot log action: User not authenticated')
            return null
        }

        try {
            const logData = {
                actorId: auth.currentUser.uid,
                actorEmail: auth.currentUser.email,
                action: action,
                entity: entity,
                entityId: entityId,
                changes: changes,
                metadata: metadata,
                createdAt: new Date().toISOString(),
                timestamp: Date.now()
            }

            const auditLogsRef = collection(db, 'auditLogs')
            const docRef = await addDoc(auditLogsRef, logData)

            return {
                id: docRef.id,
                ...logData
            }
        } catch (error) {
            console.error('Error logging action:', error)
            throw error
        }
    }

    const getEntityLogs = async (entity, entityId, maxResults = 50) => {
        try {
            const auditLogsRef = collection(db, 'auditLogs')
            const q = query(
                auditLogsRef,
                where('entity', '==', entity),
                where('entityId', '==', entityId),
                orderBy('timestamp', 'desc'),
                limit(maxResults)
            )

            const snapshot = await getDocs(q)
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
        } catch (error) {
            console.error('Error getting entity logs:', error)
            throw error
        }
    }

    const getUserLogs = async (userId, maxResults = 100) => {
        try {
            const auditLogsRef = collection(db, 'auditLogs')
            const q = query(
                auditLogsRef,
                where('actorId', '==', userId),
                orderBy('timestamp', 'desc'),
                limit(maxResults)
            )

            const snapshot = await getDocs(q)
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
        } catch (error) {
            console.error('Error getting user logs:', error)
            throw error
        }
    }

    const getRecentLogs = async (maxResults = 100) => {
        try {
            const auditLogsRef = collection(db, 'auditLogs')
            const q = query(
                auditLogsRef,
                orderBy('timestamp', 'desc'),
                limit(maxResults)
            )

            const snapshot = await getDocs(q)
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }))
        } catch (error) {
            console.error('Error getting recent logs:', error)
            throw error
        }
    }

    const getChanges = (oldData, newData, fields) => {
        const changes = {}
        
        fields.forEach(field => {
            const oldValue = oldData?.[field]
            const newValue = newData?.[field]
            
            if (oldValue !== newValue) {
                changes[field] = [oldValue, newValue]
            }
        })

        return changes
    }

    const getActionLabel = (action) => {
        const labels = {
            create_patient: 'Pacient creat',
            update_patient: 'Pacient actualizat',
            create_exam: 'Examinare creată',
            update_exam: 'Examinare actualizată',
            finalize_exam: 'Examinare finalizată',
            void_exam: 'Examinare anulată',
            export_pdf: 'PDF exportat'
        }
        return labels[action] || action
    }

    return {
        logAction,
        getEntityLogs,
        getUserLogs,
        getRecentLogs,
        getChanges,
        getActionLabel
    }
}
